// Cross-vendor "Pending Program" basket — persisted in localStorage so users
// can browse multiple vendors in Vendor Network, accumulate service selections,
// then carry them into Program Builder in a single click.
//
// Shape of a basket entry:
//   {
//     vendor_id: string,
//     vendor_name: string,
//     vendor_category?: string,
//     name: string,                    // service name
//     description?: string,
//     wholesale_cost: number,
//     suggested_retail_min?: number,
//     suggested_retail_max?: number,
//     selected_at: ISO string,
//   }

const STORAGE_KEY = "nodera-program-basket-v1";

function _safeRead() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function _safeWrite(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* storage may be disabled */
  }
  // Notify subscribers in the same tab — the storage event only fires across tabs.
  try {
    window.dispatchEvent(new CustomEvent("nodera-basket-changed"));
  } catch {
    /* SSR / no window */
  }
}

function _key(item) {
  // Service identity = vendor + service name (case insensitive). A clinic that
  // genuinely wants the same vendor+service twice can dedupe later in Program
  // Builder; we'd rather avoid accidental duplicates from re-clicking.
  return `${item.vendor_id || item.vendor_name || ""}::${(item.name || "").toLowerCase()}`;
}

export function getBasket() {
  return _safeRead();
}

export function basketCount() {
  return _safeRead().length;
}

export function isInBasket(item) {
  const k = _key(item);
  return _safeRead().some((row) => _key(row) === k);
}

export function addToBasket(item) {
  const items = _safeRead();
  const k = _key(item);
  if (items.some((row) => _key(row) === k)) return items;
  const enriched = { ...item, selected_at: new Date().toISOString() };
  const next = [...items, enriched];
  _safeWrite(next);
  return next;
}

export function removeFromBasket(item) {
  const k = _key(item);
  const next = _safeRead().filter((row) => _key(row) !== k);
  _safeWrite(next);
  return next;
}

export function toggleBasket(item) {
  return isInBasket(item) ? removeFromBasket(item) : addToBasket(item);
}

export function clearBasket() {
  _safeWrite([]);
  return [];
}

// React-y subscription for live count badges.
export function subscribeBasket(callback) {
  const handler = () => callback(_safeRead());
  window.addEventListener("nodera-basket-changed", handler);
  window.addEventListener("storage", handler); // cross-tab sync
  return () => {
    window.removeEventListener("nodera-basket-changed", handler);
    window.removeEventListener("storage", handler);
  };
}

// Convert basket entries → ProgramService payload for the create-program POST.
export function basketToProgramServices() {
  return _safeRead().map((b) => ({
    name: b.name,
    vendor_id: b.vendor_id || null,
    vendor_name: b.vendor_name || null,
    wholesale_cost: Number(b.wholesale_cost || 0),
    retail_price: Number(
      b.suggested_retail_min ||
        b.suggested_retail_max ||
        Math.round((Number(b.wholesale_cost || 0) || 50) * 2.5),
    ),
  }));
}
